/**
 * Luật thuần của màn Kết nối nguồn (port phần không vẽ của `ThemNguon.tsx`, `TheNguon.tsx`, `KetNoiView.tsx` native).
 */
import { GHI_CHU_TOI_DA } from './ghi-chu.ts';
import { MAU_GHI_CHU_KE_TOAN_VN } from './mau-ke-toan-vn.ts';
import { MAU_GHI_CHU_SAP_B1, laSapB1 } from './mau-sap-b1.ts';

/** Ví dụ chuỗi kết nối hiện ở ô dán (placeholder). */
export const MAU_DSN: Record<string, string> = {
  postgres: 'postgresql://tai_khoan:mat_khau@may-chu.khach.vn:5432/ten_csdl',
  mssql: 'mssql://tai_khoan:mat_khau@may-chu.khach.vn:1433/ten_csdl',
};

/** Cổng mặc định — cùng bảng với `CONG_MAC_DINH` của backend (`dsn`). */
export const CONG_MAC_DINH: Record<string, number> = { postgres: 5432, mssql: 1433 };

/** Lược đồ URL máy chủ nhận. */
const LUOC_DO_URL: Record<string, string> = { postgres: 'postgresql', mssql: 'mssql' };

export const TEN_PHUONG_NGU: Record<string, string> = { postgres: 'PostgreSQL', mssql: 'SQL Server' };

/** Khoảng `syncIntervalSeconds` phía máy chủ (60–86.400 giây), đổi ra phút — để ô nhập từ chối TRƯỚC khi gửi. */
export const PHUT_TOI_THIEU = 1;
export const PHUT_TOI_DA = 1440;
/** Khớp `MO_TA_TOI_DA` của backend. */
export const MO_TA_TOI_DA = 200;

export interface ThongTinNoi {
  host: string;
  cong: string;
  db: string;
  taiKhoan: string;
  matKhau: string;
}

export const NOI_TRONG: ThongTinNoi = { host: '', cong: '', db: '', taiKhoan: '', matKhau: '' };

/**
 * Ghép năm ô thành chuỗi DSN gửi lên. `encodeURIComponent` cho tài khoản và mật khẩu là bắt buộc: mật khẩu CSDL thật hay
 * có `@`, `/`, `:` — đúng những ký tự chia cắt một URL, và `p@ss/w0rd` không mã hoá sẽ cắt chuỗi làm đôi.
 */
export function dungDsn(dialect: string, tt: ThongTinNoi): string {
  const host = tt.host.trim();
  const db = tt.db.trim();
  const tk = tt.taiKhoan.trim();
  if (!host || !db || !tk) return '';
  const scheme = LUOC_DO_URL[dialect] ?? 'postgresql';
  const cong = tt.cong.trim() || String(CONG_MAC_DINH[dialect] ?? 5432);
  const mk = tt.matKhau ? `:${encodeURIComponent(tt.matKhau)}` : '';
  return `${scheme}://${encodeURIComponent(tk)}${mk}@${host}:${cong}/${db}`;
}

/**
 * Đọc ngược một chuỗi dán vào thành năm ô. Giữ đường dán vì đó là thứ quản trị CSDL gửi qua chat; dán xong mọi thứ hiện
 * ra thành ô — sửa được, và nhìn được mình sắp nối đi đâu.
 */
export function docDsn(tho: string): { dialect?: string; tt: ThongTinNoi } | null {
  try {
    const u = new URL(tho.trim());
    const scheme = u.protocol.replace(':', '').toLowerCase();
    const dialect =
      scheme === 'mssql' || scheme === 'sqlserver'
        ? 'mssql'
        : scheme === 'postgres' || scheme === 'postgresql'
          ? 'postgres'
          : undefined;
    if (!u.hostname) return null;
    return {
      dialect,
      tt: {
        host: u.hostname,
        cong: u.port,
        db: decodeURIComponent(u.pathname.replace(/^\//, '')),
        taiKhoan: decodeURIComponent(u.username),
        matKhau: decodeURIComponent(u.password),
      },
    };
  } catch {
    return null;
  }
}

/** Câu lệnh xin quản trị CSDL cấp tài khoản chỉ đọc. `{schema}` thay bằng lược đồ đang khai. */
const CAP_QUYEN: Record<string, string[]> = {
  postgres: [
    "CREATE USER doc_bao_cao PASSWORD '…';",
    'GRANT CONNECT ON DATABASE ten_csdl TO doc_bao_cao;',
    'GRANT USAGE ON SCHEMA {schema} TO doc_bao_cao;',
    'GRANT SELECT ON ALL TABLES IN SCHEMA {schema} TO doc_bao_cao;',
  ],
  mssql: [
    "CREATE LOGIN doc_bao_cao WITH PASSWORD = '…';",
    'USE ten_csdl;',
    'CREATE USER doc_bao_cao FOR LOGIN doc_bao_cao;',
    'ALTER ROLE db_datareader ADD MEMBER doc_bao_cao;',
  ],
};

export function cauLenhCapQuyen(dialect: string, luocDo: string): string {
  return (CAP_QUYEN[dialect] ?? []).map((d) => d.replaceAll('{schema}', luocDo)).join('\n');
}

/** Lược đồ mặc định khi bỏ trống ô. */
export function luocDoMacDinh(dialect: string): string {
  return dialect === 'mssql' ? 'dbo' : 'public';
}

/** Địa chỉ gốc của nguồn API phải là `http(s)://…`. */
export function laUrlApi(s: string): boolean {
  return /^https?:\/\/\S+$/.test(s.trim());
}

/** Thời gian chờ (giây) → mili giây, kẹp 1–60 giây như máy chủ. */
export function choMs(giay: number): number {
  return Math.min(60, Math.max(1, giay)) * 1000;
}

/**
 * Nhịp đồng bộ gõ tay (phút) → giá trị để gửi, hoặc `null` khi không gửi gì. Ngoài khoảng thì TRẢ VỀ giá trị cũ chứ không
 * kẹp: kẹp âm thầm nghĩa là người gõ 5000 nhận về 1440 mà không có gì nói con số họ gõ đã bị đổi.
 */
export function docNhip(nhap: string, hienTai: number): { gui: number | null; hien: string } {
  const n = Number(nhap);
  if (!Number.isInteger(n) || n < PHUT_TOI_THIEU || n > PHUT_TOI_DA) return { gui: null, hien: String(hienTai) };
  return { gui: n === hienTai ? null : n, hien: String(n) };
}

/**
 * Khai nhanh theo mẫu: văn sẽ GHI THÊM vào ghi chú nghiệp vụ. Lược đồ SAP đứng TRƯỚC phần kế toán (nó trả lời "bảng nào
 * là gì", phần kia "con số nào là gì" — cùng thứ tự với gói ngữ cảnh). Chặn TRƯỚC khi gọi máy chủ khi vượt trần, thay vì
 * để người dùng đọc xong rồi nhận một dòng 422.
 */
export function dungKhaiNhanh(
  bang: readonly string[],
  ghiChuCu: string | undefined,
): { sap: boolean; van: string; tong: number; quaDai: boolean; ghiChuMoi: string } {
  const sap = laSapB1([...bang]);
  const van = [sap ? MAU_GHI_CHU_SAP_B1 : '', MAU_GHI_CHU_KE_TOAN_VN].filter(Boolean).join('\n\n');
  const cu = (ghiChuCu ?? '').trim();
  const tong = (cu ? cu.length + 2 : 0) + van.length;
  return { sap, van, tong, quaDai: tong > GHI_CHU_TOI_DA, ghiChuMoi: cu ? `${cu}\n\n${van}` : van };
}
